/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class DashboardServiceability
 * @hideconstructor
 * @classdesc API class that is used to manage DashboardServiceability
 * (including the corrsponding view) across API calls
 */
define([
	'jquery',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/core-client/js/core-client/ui/KeyCodes',
	'./api/DashboardServiceabilityAPI'
], function($, APIFactory, KeyCodes, DashboardServiceabilityAPI){
	class DashboardServiceability{
		constructor(options){
			this._dashboardApi = options.features['API'];
			this._selectedContent = null;
			this._selectedContentIds = [];
			this._keysPressed = [];
			this._slideout = null;
			this._openPromise = null;
			this._closePromise = null;
			this._dashboardState = null;
			this._callback = function(event){ this._keyUpDownCallback(event);}.bind(this);
			this._dashboardState = options.features['DashboardState'];
			this._isInfoVisible = false;

			this._dashboardApi.getCanvasWhenReady().then((canvas) => {
				this.canvas = canvas;
				canvas.on('change:selections:select', this.onContentSelection, this);
				canvas.on('change:selections:deselect', this.onContentDeselection, this);
			});

		}

		getAPI(){
			return this._api;
		}

		initialize(){
			this._api = APIFactory.createAPI(this, [DashboardServiceabilityAPI]);
			this._dashboardState.onChangeActive(this.dashboardStateCallback.bind(this));
			return new Promise.resolve();
		}

		showS12yView(widgetInfo){
			return this._dashboardApi.showSlideOut({
				'width': 400,
				'position': 'right',
				'content': {
					'module': 'dashboard-core/js/features/dashboard/dashboardServiceability/view/ServiceabilityView',
					id: 's12ySlideoutContainer',
					widgetInfo: widgetInfo,
					dashboardApi: this._dashboardApi
				},
				resizable: {
					min: 400
				}
			});
		}

		openPanel() {
			this._closePromise = null;
			this._openPromise = Promise.resolve(false);
			if(this._selectedContent){
				let widgetIsReady = this._selectedContent.getFeature('Serviceability') === undefined ? false : true;
				if(widgetIsReady){
					this._slideout = this.showS12yView(this._selectedContent.getFeature('Serviceability').getContentInfo());
					if(this._slideout){
						this._openPromise = this._monitorOpeningSlideout();
					}
				}
			}
			return this._openPromise;
		}

		_monitorOpeningSlideout() {
			return new Promise((resolve) => {
				if (this._slideout){
					this._slideout.on('done:show', () => {
						resolve(true);
					});
					this._slideout.on('hide', () => {
						this._slideout = null;
					});
				}
			});
		}

		closePanel() {
			this._openPromise = null;
			this._closePromise = Promise.resolve();
			if(this._slideout){
				this._slideout.hide();
				this._closePromise = this._monitorClosingSlideout();
			}
			return this._closePromise;
		}

		showInfo() {
			this._isInfoVisible = true;
			this._dashboardApi.getCanvas().findContent().forEach((content) => {
				let serviceabilityFeature = content.getFeature('Serviceability');
				if(serviceabilityFeature) {
					serviceabilityFeature.showInfo();
				}
			});
		}

		hideInfo() {
			this._isInfoVisible = false;
			this._dashboardApi.getCanvas().findContent().forEach((content) => {
				let serviceabilityFeature = content.getFeature('Serviceability');
				if(serviceabilityFeature) {
					serviceabilityFeature.hideInfo();
				}
			});
		}

		/**
		 * @implements DashboardServiceabilityAPI.isRenderTimeOn
		 */
		isInfoVisible() {
			return this._isInfoVisible;
		}

		_monitorClosingSlideout() {
			return new Promise((resolve) => {
				if (this._slideout){
					this._slideout.on('hide', () => {
						this._slideout = null;
						resolve();
					});
				} else {
					resolve();
				}
			});
		}

		getPanelState(){
			const promise = this._openPromise || this._closePromise || Promise.resolve();
			return promise.then(() => {
				return {
					isOpen: this._slideout !== null
				};
			});
		}

		onContentSelection(event){
			event.info.value.forEach((id) => {
				let content = this._dashboardApi.getCanvas().getContent(id);
				if (content && content.getFeature('Serviceability')) {
					this._selectedContentIds.push(id);
				}
			});
			this._updateSelectedWidget();
		}

		/**
		 * @description One of the tasks this method needs to do is hiding info on
		 * contents when selecting another tab. Listening to `change:selections:deselect`
		 * event is necessary because re-selecting the same tab will also trigger an
		 * `change:selections:select` event hence we cannot tell if we're selecting a different
		 * tab or not. Another taks of this method is to remove some contents from the
		 * internal _selectedContent variable.
		 * @param {Object} event 
		 */
		onContentDeselection(event){
			this._selectedContentIds = this._selectedContentIds.filter((id) => {
				return  event.info.value.indexOf(id) === -1;
			});
			this._updateSelectedWidget();
			event.info.value.forEach((id) => {
				let content = this._dashboardApi.getCanvas().getContent(id);
				if(content && content.getType() === 'page') {
					this.hideInfo();
				}
			});
		}

		setSelectedWidget(widget){
			this._selectedContent = widget;
		}

		_updateSelectedWidget() {
			this._selectedContent = null;
			if(this._selectedContentIds.length == 1){
				let id = this._selectedContentIds[0];
				this.setSelectedWidget(this._dashboardApi.getCanvas().getContent(id));
			}
		}

		destroy(){
			if (this.canvas){
				this.canvas.off('change:selections:select', this.onContentSelection, this);
				this.canvas.off('change:selections:deselect', this.onContentDeselection, this);
				this.canvas = null;
			}

			$('body').off('keydown.serviceability', this._callback);
			$('body').off('keyup.serviceability', this._callback);
			this._api = null;
			this._slideout = null;
			this._selectedContentIds = null;
			this._dashboardApi = null;
			this._keysPressed = null;
			this._dashboardState = null;
		}

		dashboardStateCallback(payload){
			if(payload.info.value === true){
				$('body').on('keydown.serviceability', this._callback);
				$('body').on('keyup.serviceability', this._callback);
			}else{
				$('body').off('keydown.serviceability', this._callback);
				$('body').off('keyup.serviceability', this._callback);
			}
		}

		_keyUpDownCallback(e){
			if(e.type === 'keydown' && !(this._keysPressed.indexOf(e.keyCode) >= 0)){
				if(this._keysPressed.length === 2){
					this._keysPressed.shift();
				}
				this._keysPressed.push(e.keyCode);
			}
			if(e.type === 'keyup' && this._keysPressed.indexOf(e.keyCode) >= 0){
				let index = this._keysPressed.indexOf(e.keyCode);
				this._keysPressed.splice(index, 1);
			}
			if(this._keysReady()){
				this.openPanel();
				this._keysPressed = [];
			}
		}

		_keysReady(){
			return this._keysPressed.length === 2 &&
			this._keysPressed[0] === KeyCodes.CTRL &&
			this._keysPressed[1] === KeyCodes.PERIOD;

		}
	}
	return DashboardServiceability;
});
